import React, { FC } from "react";
import { useParams } from "react-router-dom";
import { useFormContext } from "react-hook-form";

import { Button, Icon, Loading, Typography } from "ui-library/atoms";
import { ContentBox } from "ui-library/layouts";
import { DataTable } from "ui-library/modules";

import i18n from "core/i18n";
import { ErrorMessage } from "containers/message";

import useDigisign from "../hooks/useDigisign";
import { VehicleSubjectChangeFormValues } from "../types/VehicleSubjectChangeFormValues";

export interface DigiSignRequestDocumentSectionProps {
    digitalSignatureOnlineStatus: string;
}

export const RequestDocumentSignDigiSignAuthorized: FC<DigiSignRequestDocumentSectionProps> = ({ digitalSignatureOnlineStatus }) => {
    const { t } = i18n.useTranslation();
    const { idObject } = useParams();

    const { getValues } = useFormContext<VehicleSubjectChangeFormValues>();
    const changeId = getValues("idChange");

    const { signDocument, isSigning, isSignInitializationFailed } = useDigisign(idObject, changeId);

    const isLoading = isSigning || digitalSignatureOnlineStatus === "loading";
    const isSigned = digitalSignatureOnlineStatus === "signed";

    const electronicSignatureColumns = [
        {
            field: "label",
            headerName: `${t("change.mandatoryAttachment")}`,
            flex: 3.5,
            sortable: false,
            renderCell: () => t("change.signature.documentName"),
        },
        {
            field: "status",
            headerName: `${t("vehicle.change.subject.documents.signatureStatus")}`,
            flex: 1,
            sortable: false,
            renderCell: () => {
                return isSigned ? (
                    <Icon size={16} color={"green"} icon={"circle-check"} />
                ) : (
                    <Button variant="outlined" color="blue" onClick={signDocument} disabled={isLoading}>
                        {isLoading ? <Loading loading={true} /> : t("vehicle.change.subject.documents.sign")}
                    </Button>
                );
            },
        },
    ];
    return (
        <>
            <ContentBox fullWidth textAlign={"center"} mt={4}>
                <Typography variant="h3">{t("change.signature.requestSignatures")}</Typography>
            </ContentBox>
            <ContentBox fullWidth>
                <Typography markDown={t("change.signature.digiSign.authorized.InfoText")} />
            </ContentBox>

            {isSignInitializationFailed && <ErrorMessage message={t("error.digisign.initializationFailed")} />}
            {digitalSignatureOnlineStatus === "failed" && <ErrorMessage message={t("error.digisign.signFinalizationFailed")} />}

            <ContentBox fullWidth>
                <DataTable
                    disableColumnReorder
                    rowHeight={50}
                    getRowId={(row) => row.id}
                    pageSize={10}
                    rows={[{ label: "sign", id: "123", status: "sign" }]}
                    columns={electronicSignatureColumns}
                    hideFooter
                    changeRowColorOnHover={false}
                />
            </ContentBox>
        </>
    );
};
